// Fraîcheur des données — accès au contrat `GET /api/etat-donnees` + verdict.
//
// L'app lit ses données dans une copie locale rafraîchie toutes les ~20 min.
// Ce décalage était invisible : une commande créée à l'instant n'apparaissait
// pas, et l'utilisatrice en concluait un échec. Le header l'annonce désormais
// en permanence — « Données au HH:MM » — et passe en ALERTE quand la mise à
// jour ne passe plus, parce qu'une copie muette qui gèle est exactement le
// piège qu'on veut fermer.
//
// Tout ce qui décide (ton, libellés, âge) vit ICI en fonctions pures : le
// composant ne fait que peindre, et les états se testent sans navigateur.

import { z } from 'zod';
import { ApiError, api } from './api-client';

/** Cadence du moteur de mise à jour (cron ~20 min) — annoncée à l'utilisatrice. */
export const CADENCE_MISE_A_JOUR_MIN = 20;

/**
 * Au-delà, ce n'est plus un décalage normal : deux passages consécutifs ont été
 * manqués. Marge volontaire sur la cadence (20 min) pour ne pas crier à chaque
 * exécution un peu longue — une alerte qui se déclenche pour rien n'est plus lue.
 */
export const SEUIL_ALERTE_MIN = 45;

const RessourceEnErreurSchema = z.object({
  cle: z.string(),
  libelle: z.string(),
  message: z.string().optional().default(''),
  aJourAu: z.string().nullable().optional().default(null),
});

/** Lecture tolérante (même parti pris que `bac-a-sable.ts`) : un champ manquant
 *  ne doit jamais faire disparaître l'indicateur — il doit le faire dégrader. */
export const EtatDonneesSchema = z.object({
  suivi: z.boolean().optional().default(false),
  donneesAu: z.string().nullable().optional().default(null),
  ensemblesSuivis: z.number().optional().default(0),
  ensemblesEnErreur: z.number().optional().default(0),
  enErreur: z.array(RessourceEnErreurSchema).optional().default([]),
  // Tolérant : un vieux serveur qui ne connaît pas encore ce champ (mission
  // 27/08) ne doit pas casser l'indicateur — il dégrade simplement à « aucun
  // ensemble en retard connu ».
  enRetard: z.array(RessourceEnErreurSchema).optional().default([]),
  // 03/10 — en attente d'un droit webservice : « à activer » (orange), jamais
  // une alerte ; tolérant pour un serveur qui ne connaît pas encore le champ.
  aActiver: z.array(RessourceEnErreurSchema).optional().default([]),
});
export type EtatDonnees = z.infer<typeof EtatDonneesSchema>;
export type RessourceEnErreur = z.infer<typeof RessourceEnErreurSchema>;

export const CLE_QUERY_FRAICHEUR = ['shell', 'etat-donnees'] as const;

/**
 * `null` — et non une erreur — si l'endpoint n'existe pas encore (404) ou est
 * refusé (403) : dans ces deux cas l'indicateur doit simplement ne pas exister,
 * jamais afficher une pastille cassée.
 */
export async function chargerEtatDonnees(signal?: AbortSignal): Promise<EtatDonnees | null> {
  try {
    return await api.get('/etat-donnees', EtatDonneesSchema, { signal });
  } catch (err) {
    if (err instanceof ApiError && (err.status === 404 || err.status === 403)) return null;
    throw err;
  }
}

// ─── Formatage (heure de Bruxelles, quel que soit le fuseau du poste) ────────

const fmtJour = new Intl.DateTimeFormat('fr-BE', { timeZone: 'Europe/Brussels', day: '2-digit', month: '2-digit' });
const fmtHeure = new Intl.DateTimeFormat('fr-BE', {
  timeZone: 'Europe/Brussels',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

function memeJourBruxelles(a: Date, b: Date): boolean {
  return fmtJour.format(a) === fmtJour.format(b);
}

/** `il y a 12 min` / `il y a 2 h` / `il y a 3 jours`. */
export function texteAge(minutes: number): string {
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${Math.floor(minutes)} min`;
  const heures = Math.floor(minutes / 60);
  if (heures < 24) return `il y a ${heures} h`;
  const jours = Math.floor(heures / 24);
  return jours === 1 ? 'il y a 1 jour' : `il y a ${jours} jours`;
}

/** Forme courte pour le libellé d'alerte : `2 h`, `3 jours`, `50 min`. */
function dureeCourte(minutes: number): string {
  if (minutes < 60) return `${Math.floor(minutes)} min`;
  const heures = Math.floor(minutes / 60);
  if (heures < 24) return `${heures} h`;
  const jours = Math.floor(heures / 24);
  return jours === 1 ? '1 jour' : `${jours} jours`;
}

/**
 * Phrase de la branche « Mise à jour incomplète » : distingue ce qui NE SE MET
 * PLUS À JOUR (erreur déclarée) de ce qui n'a PAS ÉTÉ RAFRAÎCHI à sa cadence
 * prévue (retard, cf. `enRetard` côté serveur) — un mot unique ne dirait pas à
 * l'utilisatrice si c'est une vraie panne ou une nuit pas encore passée.
 */
function phraseMiseAJourIncomplete(nbErreurs: number, nbRetard: number): string {
  if (nbErreurs > 0 && nbRetard > 0) {
    return `${nbErreurs + nbRetard} ensembles de données sont concernés : certains ne se mettent plus à jour, d'autres n'ont pas été rafraîchis à leur cadence prévue.`;
  }
  if (nbErreurs > 0) {
    return nbErreurs === 1
      ? 'Une partie des données ne se met plus à jour ; le reste est à jour.'
      : `${nbErreurs} ensembles de données ne se mettent plus à jour ; le reste est à jour.`;
  }
  return nbRetard === 1
    ? "Une partie des données n'a pas été rafraîchie à sa cadence prévue ; le reste est à jour."
    : `${nbRetard} ensembles de données n'ont pas été rafraîchis à leur cadence prévue ; le reste est à jour.`;
}

export type TonFraicheur = 'normal' | 'alerte' | 'inconnu';

export interface VerdictFraicheur {
  readonly ton: TonFraicheur;
  /** `13:42` (aujourd'hui) ou `12/08 17:49`. `null` si la date est inconnue/illisible. */
  readonly heure: string | null;
  readonly ageMinutes: number | null;
  readonly ageTexte: string | null;
  /** Texte du chip en grand écran. */
  readonly libelle: string;
  /** Texte du chip en petit écran — jamais plus de deux mots. */
  readonly libelleCompact: string;
  /** Titre du panneau de détail. */
  readonly titre: string;
  /** Explication en une phrase, vocabulaire d'acheteuse. */
  readonly phrase: string;
}

/**
 * Verdict complet à partir de l'état serveur et de l'instant courant.
 *
 * Une date absente ou illisible donne `inconnu` — jamais « Données au Invalid
 * Date », et jamais non plus un « tout va bien » implicite : ne pas savoir est
 * une information, qu'on affiche telle quelle.
 */
export function verdictFraicheur(etat: EtatDonnees | null, maintenant: Date = new Date()): VerdictFraicheur {
  const instant = etat?.donneesAu ? new Date(etat.donneesAu) : null;
  const dateValide = instant !== null && !Number.isNaN(instant.getTime());

  if (!etat || !etat.suivi || !dateValide) {
    return {
      ton: 'inconnu',
      heure: null,
      ageMinutes: null,
      ageTexte: null,
      libelle: 'Mise à jour inconnue',
      libelleCompact: 'Inconnue',
      titre: 'Mise à jour inconnue',
      phrase:
        "L'application ne sait pas de quand datent ces données. Prévenez l'informatique si l'indicateur reste dans cet état.",
    };
  }

  const heure = memeJourBruxelles(instant, maintenant)
    ? fmtHeure.format(instant)
    : `${fmtJour.format(instant)} ${fmtHeure.format(instant)}`;
  const ageMinutes = Math.max(0, (maintenant.getTime() - instant.getTime()) / 60_000);
  const ageTexte = texteAge(ageMinutes);
  // Ne couvre plus que les voies RAPIDES depuis le 27/08 (cf. `donneesAu` côté
  // API) : dépasser ce seuil, c'est donc bien un vrai gel, pas une nuit normale.
  const figee = ageMinutes > SEUIL_ALERTE_MIN;
  const nbErreurs = etat.enErreur.length;
  const nbRetard = etat.enRetard.length;

  if (figee) {
    return {
      ton: 'alerte',
      heure,
      ageMinutes,
      ageTexte,
      libelle: `Données figées depuis ${dureeCourte(ageMinutes)}`,
      libelleCompact: 'Données figées',
      titre: `Données figées depuis ${dureeCourte(ageMinutes)}`,
      phrase: `La mise à jour automatique ne passe plus : ce que vous voyez date du ${heure}. Les créations et modifications faites depuis n'apparaissent pas encore.`,
    };
  }

  if (nbErreurs > 0 || nbRetard > 0) {
    return {
      ton: 'alerte',
      heure,
      ageMinutes,
      ageTexte,
      libelle: 'Mise à jour incomplète',
      libelleCompact: 'Incomplète',
      titre: 'Mise à jour incomplète',
      phrase: phraseMiseAJourIncomplete(nbErreurs, nbRetard),
    };
  }

  return {
    ton: 'normal',
    heure,
    ageMinutes,
    ageTexte,
    libelle: `Données au ${heure}`,
    libelleCompact: heure,
    titre: `Données au ${heure}`,
    // L'âge exact vit dans la ligne « Dernière mise à jour » du panneau : le
    // répéter ici faisait lire deux fois la même chose.
    phrase: `Une commande ou un stock modifié à l'instant peut mettre jusqu'à ${CADENCE_MISE_A_JOUR_MIN} minutes à apparaître ici.`,
  };
}
